import axios from './axiosConfig'

export interface PuzzleAnswerCard {
  name?: string
  suit: string
  number: number | null
  arcana: string
}

export interface PuzzleHint {
  level: number
  text: string
}

export interface TarotPuzzle {
  phrase: string
  answer: {
    card_1: PuzzleAnswerCard
    card_2: PuzzleAnswerCard
  }
  hints: PuzzleHint[]
}

export interface DailyPuzzle {
  id: string
  dailyOrder: number
  completed: boolean
  puzzle: TarotPuzzle
}

export interface LastUnlimitedPuzzle {
  id: string
  puzzle: TarotPuzzle
  result: {
    won: boolean
    guessCount: number
    completedAt: string
  }
}

export interface DailyPuzzlesResponse {
  date: string
  puzzles: DailyPuzzle[]
  completedCount: number
  totalCount: number
  hasHistory?: boolean
  lastUnlimitedPuzzle?: LastUnlimitedPuzzle | null
}

export interface HistoryPuzzle {
  id: string
  completedAt: string
  won: boolean
  puzzle: TarotPuzzle
}

export interface PuzzleHistoryResponse {
  items: HistoryPuzzle[]
  nextCursor: string | null
}

export interface UnlimitedPuzzlesResponse {
  date: string | null
  puzzles: DailyPuzzle[]
  completedCount: number
  totalCount: number
  isUnlimited: true
}

export interface PuzzleResultResponse {
  result: {
    id: string
    won: boolean
    guessCount: number
    completedAt: string
  }
  puzzle: TarotPuzzle
}

/**
 * Get the puzzle date based on 9pm local cutoff.
 * Before 9pm local → today's date. After 9pm → tomorrow's date.
 */
export const getPuzzleDateString = (): string => {
  const now = new Date()
  const year = now.getFullYear()
  const month = now.getMonth()
  const day = now.getDate()
  // If it's 9pm or later, use tomorrow's date
  if (now.getHours() >= 21) {
    const tomorrow = new Date(year, month, day + 1)
    return `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`
  }
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

export const getDailyPuzzles = async (): Promise<DailyPuzzlesResponse> => {
  const localDate = getPuzzleDateString()
  const response = await axios.get(`game/daily?localDate=${localDate}`)
  return response.data
}

export const getUnlimitedPuzzles = async (): Promise<UnlimitedPuzzlesResponse> => {
  const localDate = getPuzzleDateString()
  const response = await axios.get(`game/unlimited?localDate=${localDate}`)
  return response.data
}

export const submitPuzzleResult = async (
  puzzleId: string,
  won: boolean,
  guessCount: number
): Promise<PuzzleResultResponse> => {
  const response = await axios.post('game/result', { puzzleId, won, guessCount })
  return response.data
}

export const getPuzzleHistory = async (cursor?: string | null): Promise<PuzzleHistoryResponse> => {
  const params = new URLSearchParams()
  if (cursor) params.set('cursor', cursor)
  const qs = params.toString()
  const response = await axios.get(`game/history${qs ? `?${qs}` : ''}`)
  return response.data
}

